import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, SectionLabel } from "@/components/Reveal";

export const faqs = [
  {
    q: "Que fait exactement une gestionnaire administrative indépendante ?",
    a: "Je prends en charge tout ou partie de votre administratif : facturation, dossiers, courriers, pré-comptabilité, démarches en ligne, organisation de documents… L'objectif : vous faire gagner du temps et de la sérénité, sans embaucher.",
  },
  {
    q: "Pour qui travaillez-vous ?",
    a: "Pour les professionnels (artisans, commerçants, indépendants, professions libérales, associations, TPE) comme pour les particuliers qui souhaitent être accompagnés dans leurs démarches administratives du quotidien.",
  },
  {
    q: "Comment se déroule le premier rendez-vous ?",
    a: "Le premier rendez-vous découverte est offert et sans engagement. Nous faisons le point sur votre situation et vos besoins, en présentiel ou à distance, puis je vous propose un accompagnement adapté avec un devis gratuit.",
  },
  {
    q: "Intervenez-vous dans mes locaux ou à mon domicile ?",
    a: "Oui. J'interviens sur site (vos locaux, votre domicile) ou à distance grâce à des outils numériques sécurisés, selon ce qui vous arrange le mieux.",
  },
  {
    q: "Quelle est votre zone d'intervention ?",
    a: "Je suis basée à Guiche (64) et j'interviens notamment à Bidache, Bayonne, Hasparren, Saint-Palais, Peyrehorade, Salies-de-Béarn et les communes environnantes. À distance, je peux vous accompagner où que vous soyez.",
  },
  {
    q: "Combien coûtent vos services ?",
    a: "Les tarifs sont affichés sur la page Tarifs : forfaits mensuels de 3 à 28 heures (de 135 à 1 050 € HT par mois), abonnements légers dès 25 € HT par mois, missions ponctuelles à prix fixe et forfait sur mesure si aucune formule ne convient. Sans engagement de durée, rien n'est facturé sans votre accord écrit.",
  },
  {
    q: "Mes documents et mes données sont-ils en sécurité ?",
    a: "Absolument. La confidentialité est au cœur de mon métier : vos documents sont traités avec la plus grande rigueur, et mon activité est couverte par une assurance Responsabilité Civile Professionnelle.",
  },
  {
    q: "Quelles sont vos qualifications ?",
    a: "Je suis titulaire du Titre Professionnel RPMS (Responsable de Petite et Moyenne Structure, niveau 5) et membre du réseau national AADPROX, qui m'apporte outils, méthodes, formations et veille juridique.",
  },
  {
    q: "Puis-je faire appel à vous pour une mission ponctuelle ?",
    a: "Bien sûr : remise à plat d'un classement, dossier spécifique, pic d'activité… Vous pouvez me solliciter une seule fois, sans engagement sur la durée.",
  },
  {
    q: "Comment prendre rendez-vous ?",
    a: "Directement sur ce site : répondez à quelques questions rapides, choisissez le créneau qui vous convient — il se synchronise avec mon agenda en temps réel — et vous recevez immédiatement un email de confirmation.",
  },
  {
    q: "Et si je dois annuler ou déplacer mon rendez-vous ?",
    a: "Aucun souci : répondez simplement à l'email de confirmation ou appelez-moi au 06 33 83 74 56, nous trouverons ensemble un autre créneau.",
  },
];

export default function Faq() {
  return (
    <div className="mx-auto max-w-3xl px-6 pb-24 pt-16 lg:pt-24" data-testid="faq-page">
      <Reveal className="text-center">
        <div className="flex justify-center"><SectionLabel>FAQ</SectionLabel></div>
        <h1 className="mx-auto mt-6 max-w-2xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-argia-ink sm:text-5xl lg:text-6xl" data-testid="faq-title">
          Les questions que l'on me pose souvent
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-base text-argia-muted md:text-lg">
          Tout ce qu'il faut savoir avant de réserver votre premier rendez-vous — offert et sans engagement.
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <Accordion type="single" collapsible className="mt-14 space-y-4" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`item-${i}`}
              className="rounded-2xl border border-[#2C2A29]/10 bg-white px-6 transition-shadow duration-300 data-[state=open]:shadow-md"
            >
              <AccordionTrigger
                data-testid={`faq-question-${i}`}
                className="py-5 text-left font-display text-lg font-bold text-argia-ink hover:text-argia-red hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-base leading-relaxed text-argia-muted">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-16 rounded-2xl bg-argia-ink p-10 text-center">
          <h2 className="font-display text-2xl font-bold text-white md:text-3xl">Une autre question ?</h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/70">
            Le premier rendez-vous est offert : c'est le meilleur moyen d'en parler, sans engagement.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/rendez-vous"
              data-testid="faq-cta-rdv"
              className="group inline-flex items-center gap-2 rounded-full bg-argia-red px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-argia-redDark hover:-translate-y-0.5"
            >
              Prendre rendez-vous
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+33633837456"
              data-testid="faq-cta-phone"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-argia-ink"
            >
              06 33 83 74 56
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
